import { useFetchedNotificationSettings } from '../hooks/useFetchedNotificationSettings';
import React, { PropsWithChildren, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';
import useApi from '../../api/backendApiContext';
import { NotificationSettingsDto } from '../../user/userApi';
import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';
import { useNotification } from '../notificationsContext';
import { extractBackendError } from '../../util/utils';
import useAuth from '../../auth/authContext';
import { PleaseRelogin } from '../../auth/PleaseRelogin';

const NotificationSettingsItem = (
  props: PropsWithChildren<{
    title: string;
    checked: boolean;
    disabled: boolean;
    onChanged: (enabled: boolean) => void;
  }>
) => {
  const { title, checked, disabled, onChanged, children } = props;
  const [controlId] = useState(title.toLowerCase().replace(' ', '-'));
  return (
    <div>
      <div className="w-100" style={{ maxWidth: '448px' }}>
        <div className="switch-input-container w-100">
          <input
            type="checkbox"
            className="custom-control-input"
            id={controlId}
            disabled={disabled}
            onChange={(e) => onChanged(e.target.checked)}
            checked={checked}
          />
          <label
            className="custom-control-label calendar-display-settings__check-label"
            htmlFor={controlId}
          >
            {title}
          </label>
        </div>
      </div>
      {children}
    </div>
  );
};

export const NotificationSettings = () => {
  useAnalyticsPage(Page.NOTIFICATION_SETTINGS);

  const {
    state: { user },
  } = useAuth();
  const userId = user?.userId ?? '';

  const { isLoading, isFetching, isSuccess, isError, data } =
    useFetchedNotificationSettings(userId);
  const cacheManager = useQueryCacheManager();
  const { userApi } = useApi();

  const { notifySuccess, notifyError } = useNotification();

  const { mutateAsync, isLoading: isUpdating } = useMutation(
    async (value: NotificationSettingsDto) => userApi.updateNotificationSettings(userId, value),
    {
      onMutate: (newSettings) => {
        cacheManager.setNotificationSettings({
          ...newSettings,
          version: (newSettings.version ?? 0) + 1,
        });
      },
      onError: async () => cacheManager.initNotificationSettingsRefetch(),
    }
  );

  const onEmailEnabledChanged = async (
    changes: Partial<Pick<NotificationSettingsDto, 'emailEnabled' | 'marketingEnabled'>>
  ) => {
    try {
      if (!data) return;
      if (isUpdating) {
        logger.warn('Mutation is already in progress.');
        return;
      }
      if (isFetching) {
        logger.warn("Fetching data, won't start new mutation.");
        return;
      }

      await mutateAsync({ ...data, ...changes });
      notifySuccess({
        notificationMsg: 'Notification settings updated',
        logMsg: 'Notification settings updated',
      });
    } catch (e) {
      notifyError({
        notificationMsg: 'Failed to update notification settings',
        logMsg: `Failed to update notification settings: ${extractBackendError(e)}`,
      });
    }
  };

  if (!user) return <PleaseRelogin />;
  return (
    <div>
      <div className="notification-settings__container">
        {isLoading && (
          <div className="alert alert-secondary" role="alert">
            Loading notification settings...
          </div>
        )}
        {isError && (
          <div
            className="alert alert-secondary"
            role="alert"
            data-test="user-settings__loading-error"
          >
            Error loading notification settings
          </div>
        )}
        {isSuccess && data && (
          <>
            <NotificationSettingsItem
              title="Programs and Tasks"
              checked={data.emailEnabled}
              disabled={isUpdating}
              onChanged={async (enabled) => onEmailEnabledChanged({ emailEnabled: enabled })}
            >
              <div className="growegy-color-neutral-500">
                <span>You’ll receive emails when:</span>
                <ul className="text-bullet-list">
                  <li>·&nbsp;&nbsp;A program owned by you starts tomorrow</li>
                  <li>·&nbsp;&nbsp;A task assigned to you is due tomorrow</li>
                  <li>·&nbsp;&nbsp;A task within a program owned by you is due tomorrow</li>
                </ul>
              </div>
            </NotificationSettingsItem>
            <NotificationSettingsItem
              title="Marketing and Promotions"
              checked={data.marketingEnabled}
              disabled={isUpdating}
              onChanged={async (enabled) => onEmailEnabledChanged({ marketingEnabled: enabled })}
            >
              <div className="growegy-color-neutral-500">
                <span>You’ll receive emails about:</span>
                <ul className="text-bullet-list">
                  <li>·&nbsp;&nbsp;New features and product updates</li>
                  <li>·&nbsp;&nbsp;Tips and best practices to get more out of Growegy</li>
                  <li>·&nbsp;&nbsp;Special offers, promotions, and events</li>
                </ul>
              </div>
            </NotificationSettingsItem>
          </>
        )}
      </div>
    </div>
  );
};
